import React, { useEffect, useMemo } from 'react';
import GenericSelect from '../../GenericSelect/GenericSelect';
import { AvailableIcons, Icon } from 'src/components-dummy';
import { SelectChangeEvent, Skeleton } from '@mui/material';
import { metadataActions } from 'src/components-bl/Analytics/actions';
import { useDispatch } from 'react-redux';
import { AppThunkDispatch } from 'src/app-types';
import { useAppSelector } from 'src/hooks';
import { ProductsMetadataState } from 'src/reducers';

interface IProductSelectorProps {
  product: string;
  setProduct: (product: string) => void;
  shopId: number;
}

const ALL_PRODUCTS_OPTION = 'All Syte Products';

export const ProductSelector = ({ product, setProduct, shopId }: IProductSelectorProps) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const productsMetadata = (useAppSelector(
    state => state.dataInsights.productsMetadata
  ) as ProductsMetadataState) || { data: [], loading: false, error: null };
  const isLoading = productsMetadata.loading;

  const handleChange = (event: SelectChangeEvent<string>) => {
    setProduct(event.target.value);
  };

  const productOrder = [
    ALL_PRODUCTS_OPTION,
    'Discovery Button',
    'Similar Items',
    'Shop The Look',
    'Bought Together',
    'Personalization',
    'Collections',
    'Text Search',
    'Camera',
    'Website',
  ];
  const labelOverrides: Record<string, string> = {
    'Discovery Button': 'Discovery Icon',
    'Similar Items': 'Similar Items Carousel',
    'Shop The Look': 'Shop the Look Carousel',
    Camera: 'Camera Search',
  };
  const normalizedKey = (value: string) => value.trim().toLowerCase();
  const orderIndex = new Map(productOrder.map((name, idx) => [normalizedKey(name), idx]));

  const sortProducts = (products: string[]) => {
    return [...products].sort((a, b) => {
      const aKey = normalizedKey(a);
      const bKey = normalizedKey(b);
      const aRank = orderIndex.get(aKey);
      const bRank = orderIndex.get(bKey);
      if (aRank != null && bRank != null) return aRank - bRank;
      if (aRank != null) return -1;
      if (bRank != null) return 1;
      return a.localeCompare(b);
    });
  };

  useEffect(() => {
    dispatch(metadataActions.getProductsMetadata({ shopId }));
  }, [dispatch, shopId]);

  const mapProductNamesToMenu = (productNames: string[]) => {
    const uniqueProductNames = Array.from(
      new Set([
        ALL_PRODUCTS_OPTION,
        ...productNames.filter(name => !['all', 'all syte products'].includes(normalizedKey(name))),
      ])
    );

    return sortProducts(uniqueProductNames).map(name => {
      return {
        value: name,
        label: labelOverrides[name] || name,
      };
    });
  };

  const productNamesSelectOptions = useMemo(() => {
    return mapProductNamesToMenu(productsMetadata.data || []);
  }, [productsMetadata]);

  // The product filter can be restored from the URL (NAYAX-122). Once the shop's product
  // list has actually loaded, reset to the default if the restored value isn't a real option
  // (prevents an out-of-range MUI Select). Guarded on a populated list so a valid-but-not-yet-
  // loaded product is never wrongly reset during the loading window.
  useEffect(() => {
    const data = productsMetadata.data || [];
    if (productsMetadata.loading || data.length === 0) {
      return;
    }
    const isKnownProduct = productNamesSelectOptions.some(option => option.value === product);
    if (!isKnownProduct) {
      setProduct(ALL_PRODUCTS_OPTION);
    }
  }, [
    productsMetadata.loading,
    productsMetadata.data,
    productNamesSelectOptions,
    product,
    setProduct,
  ]);

  return isLoading ? (
    <Skeleton variant='rectangular' width={160} height={26} sx={{ borderRadius: 1 }} />
  ) : (
    <GenericSelect<string>
      value={product}
      onChange={handleChange}
      options={productNamesSelectOptions}
      icon={<Icon name={AvailableIcons.FilterV2} className='added-icon' />}
      placeholder='Select product...'
      menuWidth={240}
    />
  );
};
